import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "salesive-api-axios";

export const PendingReviewsSlider = forwardRef(({ darkMode = false }, ref) => {
    const [isVisible, setIsVisible] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Review form state
    const [rating, setRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [comment, setComment] = useState("");

    useImperativeHandle(ref, () => ({
        open: () => {
            fetchPendingReviews(true); // Force open, bypass delay check
        },
        close: () => handleClose(),
    }));

    // Auto-show after 10 seconds on page load
    useEffect(() => {
        const STORAGE_KEY = "salesive_reviews_shown_at";
        const ONE_DAY = 24 * 60 * 60 * 1000;

        const lastShown = localStorage.getItem(STORAGE_KEY);
        if (lastShown) {
            const timeSince = Date.now() - Number(lastShown);
            if (timeSince < ONE_DAY) {
                // Don't show again until next day
                return;
            }
        }

        const timer = setTimeout(() => {
            fetchPendingReviews(false);
        }, 10000); // 10 seconds

        return () => clearTimeout(timer);
    }, []);

    const fetchPendingReviews = async (forceOpen = false) => {
        // If not forcing, check the delay
        if (!forceOpen) {
            const STORAGE_KEY = "salesive_reviews_shown_at";
            const ONE_DAY = 24 * 60 * 60 * 1000;
            const lastShown = localStorage.getItem(STORAGE_KEY);
            if (lastShown) {
                const timeSince = Date.now() - Number(lastShown);
                if (timeSince < ONE_DAY) {
                    return;
                }
            }
        }

        try {
            const response = await api.get("/reviews/pending");
            const pendingProducts = response?.data?.data?.pendingProducts || [];

            if (pendingProducts.length > 0) {
                setReviews(pendingProducts);
                setCurrentIndex(0);
                resetForm();
                setIsVisible(true);
            }
        } catch (err) {
            console.error(
                "[SalesiveWidget] Failed to fetch pending reviews",
                err,
            );
        }
    };

    const resetForm = () => {
        setRating(0);
        setHoveredRating(0);
        setComment("");
    };

    const handleClose = () => {
        setIsVisible(false);
        resetForm();
        // Save timestamp to delay next auto-show until next day
        localStorage.setItem(
            "salesive_reviews_shown_at",
            Date.now().toString(),
        );
    };

    const handleSkip = () => {
        if (currentIndex < reviews.length - 1) {
            setCurrentIndex(currentIndex + 1);
            resetForm();
        } else {
            handleClose();
        }
    };

    const handleSubmit = async () => {
        if (rating === 0) return;

        const currentItem = reviews[currentIndex];
        if (!currentItem) return;

        setIsSubmitting(true);
        try {
            await api.post("/reviews", {
                orderId: currentItem.orderId,
                productId: currentItem.product?._id,
                rating,
                title: "",
                comment: comment.trim(),
                images: [],
            });

            // Move to next or close
            if (currentIndex < reviews.length - 1) {
                setCurrentIndex(currentIndex + 1);
                resetForm();
            } else {
                handleClose();
            }
        } catch (err) {
            console.error("[SalesiveWidget] Failed to submit review", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentItem = reviews[currentIndex];

    return (
        <AnimatePresence>
            {isVisible && currentItem && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="tw-fixed tw-inset-0 tw-bg-black/50 tw-z-[2147483647]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                    />

                    {/* Modal */}
                    <motion.div
                        className={`
                            tw-fixed tw-z-[2147483648] tw-shadow-2xl tw-overflow-hidden
                            tw-inset-x-4 tw-bottom-4 tw-top-auto
                            sm:tw-inset-auto sm:tw-top-1/2 sm:tw-left-1/2 sm:tw-w-[400px]
                            tw-rounded-2xl
                            ${darkMode ? "tw-bg-gray-800 tw-text-white" : "tw-bg-white tw-text-gray-900"}
                        `}
                        initial={{ opacity: 0, y: 100, scale: 0.95 }}
                        animate={{
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            x:
                                typeof window !== "undefined" &&
                                window.innerWidth >= 640
                                    ? "-50%"
                                    : 0,
                            ...(typeof window !== "undefined" &&
                            window.innerWidth >= 640
                                ? { y: "-50%" }
                                : {}),
                        }}
                        exit={{ opacity: 0, y: 100, scale: 0.95 }}
                        transition={{
                            type: "spring",
                            damping: 25,
                            stiffness: 300,
                        }}
                    >
                        {/* Progress indicator */}
                        <div className={`tw-flex tw-gap-1 tw-p-3 tw-pb-0`}>
                            {reviews.map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`tw-flex-1 tw-h-1 tw-rounded-full tw-transition-colors ${
                                        idx <= currentIndex
                                            ? "tw-bg-primary"
                                            : darkMode
                                              ? "tw-bg-gray-700"
                                              : "tw-bg-gray-200"
                                    }`}
                                />
                            ))}
                        </div>

                        {/* Header */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
                            <span
                                className={`tw-text-xs ${darkMode ? "tw-text-gray-400" : "tw-text-gray-500"}`}
                            >
                                {currentIndex + 1} of {reviews.length}
                            </span>
                            <span
                                onClick={handleClose}
                                className={`tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-cursor-pointer tw-transition-colors ${
                                    darkMode
                                        ? "hover:tw-bg-gray-700"
                                        : "hover:tw-bg-gray-100"
                                }`}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </span>
                        </div>

                        {/* Product Info */}
                        <div className="tw-px-4 tw-pb-4">
                            <div className="tw-flex tw-gap-4 tw-items-start">
                                {/* Product Image */}
                                <div
                                    className={`tw-w-20 tw-h-20 tw-rounded-xl tw-flex-shrink-0 tw-overflow-hidden ${
                                        darkMode
                                            ? "tw-bg-gray-700"
                                            : "tw-bg-gray-100"
                                    }`}
                                >
                                    {currentItem.imageUrl ? (
                                        <img
                                            src={currentItem.imageUrl}
                                            alt={currentItem.product?.name}
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    ) : (
                                        <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="tw-w-6 tw-h-6 tw-text-gray-400"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <rect
                                                    x="3"
                                                    y="3"
                                                    width="18"
                                                    height="18"
                                                    rx="2"
                                                    ry="2"
                                                />
                                                <circle
                                                    cx="8.5"
                                                    cy="8.5"
                                                    r="1.5"
                                                />
                                                <polyline points="21 15 16 10 5 21" />
                                            </svg>
                                        </div>
                                    )}
                                </div>

                                <div className="tw-flex-1 tw-min-w-0">
                                    <h3
                                        className={`tw-font-semibold tw-text-sm tw-leading-tight ${darkMode ? "tw-text-gray-100" : "tw-text-gray-900"}`}
                                    >
                                        {currentItem.product?.name || "Product"}
                                    </h3>

                                    {/* Variant */}
                                    {currentItem.variantAttributes &&
                                        Object.keys(
                                            currentItem.variantAttributes,
                                        ).length > 0 && (
                                            <div className="tw-flex tw-flex-wrap tw-gap-1 tw-mt-1.5">
                                                {Object.entries(
                                                    currentItem.variantAttributes,
                                                ).map(([key, value]) => (
                                                    <span
                                                        key={key}
                                                        className={`tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-full tw-capitalize ${
                                                            darkMode
                                                                ? "tw-bg-gray-700 tw-text-gray-300"
                                                                : "tw-bg-gray-100 tw-text-gray-600"
                                                        }`}
                                                    >
                                                        {value}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                    <p
                                        className={`tw-text-xs tw-mt-1.5 ${darkMode ? "tw-text-gray-400" : "tw-text-gray-500"}`}
                                    >
                                        Order #{currentItem.orderNumber}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Divider */}
                        <div
                            className={`tw-h-px ${darkMode ? "tw-bg-gray-700" : "tw-bg-gray-100"}`}
                        />

                        {/* Rating Section */}
                        <div className="tw-p-4">
                            <p
                                className={`tw-text-sm tw-font-medium tw-text-center tw-mb-3 ${darkMode ? "tw-text-gray-200" : "tw-text-gray-700"}`}
                            >
                                How was your experience?
                            </p>

                            {/* Star Rating */}
                            <div className="tw-flex tw-justify-center tw-gap-2 tw-mb-4">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                        key={star}
                                        onClick={() => setRating(star)}
                                        onMouseEnter={() =>
                                            setHoveredRating(star)
                                        }
                                        onMouseLeave={() => setHoveredRating(0)}
                                        className="tw-cursor-pointer tw-transition-transform hover:tw-scale-110"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="32"
                                            height="32"
                                            viewBox="0 0 24 24"
                                            fill={
                                                (hoveredRating || rating) >=
                                                star
                                                    ? "#FBBF24"
                                                    : "none"
                                            }
                                            stroke={
                                                (hoveredRating || rating) >=
                                                star
                                                    ? "#FBBF24"
                                                    : darkMode
                                                      ? "#4B5563"
                                                      : "#D1D5DB"
                                            }
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                        </svg>
                                    </span>
                                ))}
                            </div>

                            {/* Comment */}
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Share your thoughts (optional)"
                                rows={3}
                                className={`tw-w-full tw-px-3 tw-py-2.5 tw-text-sm tw-rounded-xl tw-border tw-resize-none tw-outline-none tw-transition-colors ${
                                    darkMode
                                        ? "tw-bg-gray-700 tw-border-gray-600 tw-text-white placeholder:tw-text-gray-400 focus:tw-border-primary"
                                        : "tw-bg-gray-50 tw-border-gray-200 tw-text-gray-900 placeholder:tw-text-gray-400 focus:tw-border-primary"
                                }`}
                            />
                        </div>

                        {/* Footer Actions */}
                        <div className="tw-flex tw-gap-3 tw-p-4 tw-pt-0">
                            <span
                                onClick={handleSkip}
                                className={`tw-flex-1 tw-py-2.5 tw-text-center tw-text-sm tw-font-medium tw-rounded-xl tw-cursor-pointer tw-transition-colors ${
                                    darkMode
                                        ? "tw-text-gray-400 hover:tw-bg-gray-700"
                                        : "tw-text-gray-500 hover:tw-bg-gray-100"
                                }`}
                            >
                                Skip
                            </span>
                            <span
                                onClick={handleSubmit}
                                className={`tw-flex-1 tw-py-2.5 tw-text-center tw-text-sm tw-font-medium tw-rounded-xl tw-cursor-pointer tw-transition-colors tw-text-white ${
                                    rating === 0 || isSubmitting
                                        ? "tw-bg-primary/60 tw-cursor-not-allowed"
                                        : "tw-bg-primary hover:tw-bg-primary/90"
                                }`}
                            >
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </span>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
});
